import { useRef, useState } from 'react';

import { Section } from '@/components/ui/Section';
import {
  buildImageBackground,
  buildLinearGradient,
  getGradientBackground,
  getImageBackground,
  getSlideBackgroundKind,
  getSolidBackground,
  DEFAULT_SOLID,
  SLIDE_GRADIENT_PRESETS,
  type SlideImageBackground,
  type SlideLinearGradient,
} from '@/lib/slide-background';
import { uploadFile } from '@/lib/uploads';
import { cn } from '@/lib/utils';

interface Props {
  value?: string;
  onChange: (background: string) => void;
}

/** Slide background editor: solid colour, two-stop linear gradient, or a
 *  full-bleed image (uploaded or pasted by URL). */
export function SlideBackgroundEditor({ value, onChange }: Props) {
  // Image mode with no image yet has nothing valid to persist, so tab choice
  // is kept locally until an image URL exists (the stored kind then wins).
  const [imageTab, setImageTab] = useState(false);
  const storedKind = getSlideBackgroundKind(value);
  const kind = storedKind === 'image' || imageTab ? 'image' : storedKind;
  const solid = getSolidBackground(value);
  const gradient = getGradientBackground(value);
  const image = getImageBackground(value);

  const setGradient = (g: SlideLinearGradient) => onChange(buildLinearGradient(g));
  const pickTab = (next: 'solid' | 'gradient' | 'image') => {
    setImageTab(next === 'image');
    if (next === 'solid') onChange(solid);
    else if (next === 'gradient') setGradient(gradient);
    // 'image': persist nothing until an image is chosen/pasted.
  };

  return (
    <Section title="Background">
      <div className="mb-2 inline-flex rounded-md border border-border p-0.5 text-xs">
        <Tab active={kind === 'solid'} onClick={() => pickTab('solid')}>
          Solid
        </Tab>
        <Tab active={kind === 'gradient'} onClick={() => pickTab('gradient')}>
          Gradient
        </Tab>
        <Tab active={kind === 'image'} onClick={() => pickTab('image')}>
          Image
        </Tab>
      </div>

      {kind === 'image' ? (
        <ImageBackgroundPanel
          image={image}
          onChange={onChange}
          onRemove={() => {
            setImageTab(false);
            onChange(DEFAULT_SOLID);
          }}
        />
      ) : kind === 'solid' ? (
        <div className="flex h-9 items-center gap-2 rounded-md border border-border bg-white pl-2 pr-3">
          <input
            type="color"
            value={solid}
            onChange={(e) => onChange(e.target.value)}
            className="h-6 w-7 cursor-pointer rounded-sm border border-border bg-transparent"
            aria-label="Background color"
          />
          <input
            value={solid}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 bg-transparent text-sm text-fg outline-none"
          />
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <GradientColor
              label="From"
              value={gradient.from}
              onChange={(from) => setGradient({ ...gradient, from })}
            />
            <GradientColor
              label="To"
              value={gradient.to}
              onChange={(to) => setGradient({ ...gradient, to })}
            />
          </div>
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-fg-muted">
              Angle — {gradient.angle}°
            </span>
            <input
              type="range"
              min={0}
              max={360}
              value={gradient.angle}
              onChange={(e) => setGradient({ ...gradient, angle: Number(e.target.value) })}
              className="w-full"
            />
          </label>
          <div className="flex flex-wrap gap-1.5">
            {SLIDE_GRADIENT_PRESETS.map((p) => (
              <button
                key={p.label}
                title={p.label}
                onClick={() => setGradient(p.gradient)}
                className="h-7 w-10 rounded-md border border-border"
                style={{ background: buildLinearGradient(p.gradient) }}
              />
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

function ImageBackgroundPanel({
  image,
  onChange,
  onRemove,
}: {
  image: SlideImageBackground;
  onChange: (background: string) => void;
  onRemove: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [urlDraft, setUrlDraft] = useState(image.url);

  const apply = (next: SlideImageBackground) => {
    if (!next.url.trim()) return;
    onChange(buildImageBackground(next));
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const uploaded = await uploadFile(file, { type: 'ADMIN' });
      setUrlDraft(uploaded.url);
      apply({ url: uploaded.url, fit: image.fit });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      {image.url ? (
        <div
          className="h-20 w-full rounded-md border border-border"
          style={{ background: buildImageBackground(image) }}
          title={image.url}
        />
      ) : (
        <div className="flex h-20 w-full items-center justify-center rounded-md border border-dashed border-border text-[11px] text-fg-subtle">
          No image yet — upload or paste a URL
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="flex-1 rounded-md border border-border bg-white px-2 py-1.5 text-xs font-medium text-fg hover:bg-surface disabled:opacity-50"
        >
          {busy ? 'Uploading…' : image.url ? 'Replace image…' : 'Upload image…'}
        </button>
        {image.url && (
          <button
            onClick={onRemove}
            className="rounded-md border border-border bg-white px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
          >
            Remove
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />

      <label className="block">
        <span className="mb-1 block text-[11px] font-medium text-fg-muted">Image URL</span>
        <input
          value={urlDraft}
          onChange={(e) => setUrlDraft(e.target.value)}
          onBlur={() => apply({ url: urlDraft, fit: image.fit })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          placeholder="https://…"
          className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs text-fg outline-none focus:border-accent"
        />
      </label>

      {image.url && (
        <div className="inline-flex rounded-md border border-border p-0.5 text-xs">
          <Tab active={image.fit === 'cover'} onClick={() => apply({ ...image, fit: 'cover' })}>
            Fill
          </Tab>
          <Tab active={image.fit === 'contain'} onClick={() => apply({ ...image, fit: 'contain' })}>
            Fit
          </Tab>
        </div>
      )}

      {error && <p className="text-[11px] text-red-600">{error}</p>}
    </div>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded px-2.5 py-1 font-medium transition',
        active ? 'bg-accent text-white' : 'text-fg-muted hover:text-fg',
      )}
    >
      {children}
    </button>
  );
}

function GradientColor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-fg-muted">{label}</span>
      <div className="flex h-9 items-center gap-2 rounded-md border border-border bg-white pl-2 pr-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-6 w-7 cursor-pointer rounded-sm border border-border bg-transparent"
          aria-label={`${label} color`}
        />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent text-xs text-fg outline-none"
        />
      </div>
    </label>
  );
}
